import React from 'react';
import { useStore, useReactFlow, useViewport } from '@xyflow/react';
import type { Node } from '@xyflow/react';
import { getHelperLines, HelperLine } from '../../utils/helperLines';

/**
 * HelperLines component that shows alignment guides when dragging nodes
 */
export function HelperLines() {
  const { getNodes } = useReactFlow();
  const viewport = useViewport();
  
  // Get the currently dragging node
  const draggingNode = useStore((state) => {
    // Find the node that is currently being dragged
    const nodes = Array.from(state.nodeLookup.values());
    return nodes.find((node) => node.dragging);
  });

  const [helperLines, setHelperLines] = React.useState<{
    horizontal?: HelperLine;
    vertical?: HelperLine;
  }>({});

  React.useEffect(() => {
    if (!draggingNode) {
      setHelperLines({});
      return;
    }

    const nodes = getNodes();
    const nodeWidth = (draggingNode.measured?.width || draggingNode.width) ?? 150;
    const nodeHeight = (draggingNode.measured?.height || draggingNode.height) ?? 40;

    const result = getHelperLines(draggingNode, nodes, nodeWidth, nodeHeight);
    
    setHelperLines({
      horizontal: result.horizontal,
      vertical: result.vertical,
    });
  }, [draggingNode, getNodes]);

  if (!draggingNode) {
    return null;
  }

  // Convert flow coordinates to screen coordinates
  const toScreenX = (x: number) => x * viewport.zoom + viewport.x;
  const toScreenY = (y: number) => y * viewport.zoom + viewport.y;

  return (
    <svg
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {helperLines.horizontal && (
        <line
          x1="0"
          y1={toScreenY(helperLines.horizontal.position)}
          x2="100%"
          y2={toScreenY(helperLines.horizontal.position)}
          stroke="#3b82f6"
          strokeWidth="1.5"
          strokeDasharray="5,5"
        />
      )}
      {helperLines.vertical && (
        <line
          x1={toScreenX(helperLines.vertical.position)}
          y1="0"
          x2={toScreenX(helperLines.vertical.position)}
          y2="100%"
          stroke="#3b82f6"
          strokeWidth="1.5"
          strokeDasharray="5,5"
        />
      )}
    </svg>
  );
}
